import type { User } from "@auth0/auth0-react";
import type { UserWithDetails } from "../services/userService";

export const maskEmail = (email: string): string => {
	const [localPart, domain] = email.split("@");
	if (localPart.length <= 2) {
		return `${localPart[0]}***@${domain}`;
	}
	return `${localPart[0]}***${localPart[localPart.length - 1]}@${domain}`;
};

export const maskPhoneNumber = (phoneNumber: string): string => {
	// Remove any spaces, dashes, or formatting
	const cleanPhone = phoneNumber.replace(/\D/g, "");

	if (cleanPhone.length <= 4) {
		return `${cleanPhone.slice(0, 2)}***`;
	}

	// Show first 3 digits and last 4 digits with *** in between
	// For example: +1234567890 -> +123***7890
	const firstPart = cleanPhone.slice(0, 3);
	const lastPart = cleanPhone.slice(-4);
	return `+${firstPart}***${lastPart}`;
};

export const isCodeComplete = (code: string[]): boolean => {
	return code.every((digit) => digit !== "");
};

export const getCodeString = (code: string[]): string => {
	return code.join("");
};

export interface TwoFactorDefaultMethodResult {
	defaultMethod: "phone" | "email";
	reason: "has_phone" | "no_phone" | "fallback_error";
	error?: string;
}

/**
 * Determines the default 2FA method based on user's phone number availability
 * Returns phone if user has a phone number, email otherwise.
 *
 * The caller supplies `resolveUserDetails` so the user profile is read through
 * the shared React Query cache (`userQueryOptions`) instead of firing its own
 * `/api/user` request — this de-duplicates the login-step fetch against the rest
 * of the app (UserDataLoader, providers, etc.).
 */
export async function determineDefault2FAMethod(
	user: User | undefined,
	logAuth: (event: string, metadata?: Record<string, unknown>) => void,
	resolveUserDetails: () => Promise<UserWithDetails>,
): Promise<TwoFactorDefaultMethodResult> {
	const userEmail = user?.email ? maskEmail(user.email) : "unknown";

	try {
		const userWithDetails = await resolveUserDetails();
		const hasPhone = !!userWithDetails.details?.phoneNumber?.trim();
		const defaultMethod: "phone" | "email" = hasPhone ? "phone" : "email";
		const reason = hasPhone ? "has_phone" : "no_phone";

		// Analytics: Track default method selection
		logAuth("2fa_default_method_determined", {
			defaultMethod,
			reason,
			userEmail,
		});

		return {
			defaultMethod,
			reason,
		};
	} catch (error) {
		const errorMessage = error instanceof Error ? error.message : "unknown";

		// Analytics: Track failure to determine default method
		logAuth("2fa_default_method_fallback", {
			fallbackMethod: "email",
			error: errorMessage,
			userEmail,
		});

		return {
			defaultMethod: "email",
			reason: "fallback_error",
			error: errorMessage,
		};
	}
}
